import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiFrecuentlyBoughtTogetherConfig, ApiFrecuentlyBoughtTogetherResponse } from '../../domain/models/api-frecuently-bought-together.model';
import { ApiOtherYouMayLikeResponse } from '../../domain/models/api-other-you-may-like.model';
import { ApiRecommendedForYouConfig, ApiRecommendedForYouResponse } from '../../domain/models/api-recommended-for-you.model';
import { ApiTopSalesConfig, ApiTopSalesTrendsResponse, ApiTopSalesRecommendationsResponse } from '../../domain/models/api-top-sales.model';
import { ApiCompleteLookYodaConfig, ApiCompleteLookYodaResponse, SectionNameYoda } from '../../domain/models/complete-look-yoda.model';
import {
    ApiRecommendationsByPlacementConfig, ApiRecommendationsByPlacementSuccessResponse, RecommendationsByPlacementParams,
} from '../../domain/models/recommendations-by-placement.model';

import { HttpRequestsService, RequestParams } from '@core/infrastructure/http-requests/http-requests.service';
import { environment } from '@env/environment';

@Injectable()
export class ApiRelatedProductsService {
    constructor(
        private readonly http: HttpClient,
        private readonly requestService: HttpRequestsService
    ) {}

    // Recommended For You
    private readonly recommendedForYouConfig: ApiRecommendedForYouConfig = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${environment.endpointRecommendationsTinybirdToken}`,
        },
        params: {
            tenant_id: 'md',
            max_products: 60,
        },
    };

    public getRecommendedForYouProducts$(
        storeId: string,
        hashedMail: string,
        partNumber: string,
        section: number
    ): Observable<ApiRecommendedForYouResponse> {
        const url: string = environment.endpointRecommendationsForYouProduct;
        this.recommendedForYouConfig.params = {
            ...this.recommendedForYouConfig.params,
            store_id: storeId,
            email_sha256: hashedMail,
            product_id: partNumber,
            cod_section: section,
        };

        return this.http.get<ApiRecommendedForYouResponse>(url, this.recommendedForYouConfig);
    }

    // End

    // TOP SALES
    private readonly topSalesConfig: ApiTopSalesConfig = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${environment.endpointRecommendationsTinybirdToken}`,
        },
        params: {
            tenant_id: 'md',
            max_products: 60,
        },
    };

    public getTopSalesTrendsProducts$(
        storeId: string,
        section: number,
    ): Observable<ApiTopSalesTrendsResponse> {
        const url: string = environment.endpointTopSalesTrendsUrl;
        const topSalesConfig: ApiTopSalesConfig = {
            ...this.topSalesConfig,
            params: {
                ...this.topSalesConfig.params,
                store_id: storeId,
                ...section && { cod_section: section },
            },
        };

        return this.http.get<ApiTopSalesTrendsResponse>(url, topSalesConfig);
    }

    public getTopSalesRecommendationProducts$(
        storeId: string,
        section: number,
        campaign: string,
        hashedMail: string,
    ): Observable<ApiTopSalesRecommendationsResponse> {
        const url: string = environment.endpointEpRecommendationsUrl;
        this.topSalesConfig.params = {
            ...this.topSalesConfig.params,
            store_id: storeId,
            cod_section: section,
            campaign,
            email_sha256: hashedMail || 'generic_recommendation',
            max_products: 200,
        };

        return this.http.get<ApiTopSalesRecommendationsResponse>(url, this.topSalesConfig);
    }

    // END

    // region Frecuently Bought Together

    private readonly frencuentlyBoughtConfig: ApiFrecuentlyBoughtTogetherConfig = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${environment.endpointRecommendationsTinybirdToken}`,
        },
        params: {
            tenant_id: 'md',
            max_products: '60',
        },
    };

    public getFrencuentlyBoughtProducts$(
        productIds: string[],
        storeId: string
    ): Observable<ApiFrecuentlyBoughtTogetherResponse> {
        const url: string = environment.endpointRecommendationsFbtUrl;
        this.frencuentlyBoughtConfig.params = {
            ...this.frencuentlyBoughtConfig.params,
            product_id: productIds.join(','),
            store_id: storeId,
        };

        return this.http.get<ApiFrecuentlyBoughtTogetherResponse>(url, this.frencuentlyBoughtConfig);
    }

    // endregion Frecuently Bought Together

    // #region Complete Look Yoda

    private readonly completeLookYodaConfig: ApiCompleteLookYodaConfig = {
        headers: {
            'Content-Type': 'application/json',
        },
        params: {
            productLimit: 60,
            showProducts: false,
        },
    };

    /**
     * Returns the complete info of products recommended for a given placement
     *
     * @param campaign the campaign to use for the recommendation
     * @param sectionName the section name to filter the recommendations. (Unused for now but could be used in the future)
     * @param productId id of the product
     * @param storeId the market id
     * @see https://sscc.central.inditex.grp/apiportal/web/products/navigsf/protocols/rest/apis/product-collections/latest/docs/technical-documentation/RecommendationsMultiTenant/operation/genericRecommendations
     * @returns a list of products recommended for the given placement and product id
     */
    public getCompleteLookYoda$(
        campaign: string,
        _sectionName: SectionNameYoda,
        productId: string,
        storeId: string,
    ): Observable<ApiCompleteLookYodaResponse> {
        // Placement could be: cart_page, cart_toast, grid_inaugural, grid_page, product_page, search_page
        const placement = 'complete_look';

        const slug = `catalog/store/${storeId}/placement/${placement}/personalization`;

        const requestParameters: RequestParams = [
            {
                key: 'productIds',
                value: productId,
            },
        ];

        const apiVersion = '1';

        const url = this.requestService.requestUrl(slug, null, requestParameters, false, apiVersion);

        this.completeLookYodaConfig.params = {
            ...this.completeLookYodaConfig.params,
            campaign,
            // sectionName,
        };
        return this.http.get<ApiCompleteLookYodaResponse>(url, this.completeLookYodaConfig);
    }

    // #endregion Complete Look Yoda

    private readonly recommendationsByPlacementConfig: ApiRecommendationsByPlacementConfig = {
        headers: {
            'Content-Type': 'application/json',
        },
        params: {
            showProducts: false,
        },
    };

    public getRecommendationsByPlacement$(
        params: RecommendationsByPlacementParams,
    ): Observable<ApiRecommendationsByPlacementSuccessResponse> {
        const slug = `catalog/store/${params.storeId}/placement/${params.placement}/personalization`;
        const url = this.requestService.requestUrl(slug, null, null, false, '1');
        const productIds = params.productIds?.length ? params.productIds : null;
        const locale = params.locale;
        const sessionId = params.sessionId;
        const campaign = params.campaign;
        const sectionName = params.sectionName;
        const strategy = params.strategy;
        const userId = params.userId;
        const productLimit = params.limit;
        const categoryId = params.categoryId;
        const visitorId = params.visitorId;

        const config: ApiRecommendationsByPlacementConfig = {
            ...this.recommendationsByPlacementConfig,
            params: {
                ...this.recommendationsByPlacementConfig.params,
                ...productIds && { productIds: productIds.join(',') },
                ...strategy && { strategy },
                ...locale && { locale },
                ...sessionId && { sessionId },
                ...campaign && { campaign },
                ...sectionName && { sectionName },
                ...userId && userId > -1 && { userId: userId.toString() },
                ...productLimit && { productLimit },
                ...categoryId && { categoryId: +categoryId },
                ...visitorId && { visitorId },
            },
        };

        return this.http.get<ApiRecommendationsByPlacementSuccessResponse>(url, config);
    }

    private readonly otherYouMayLikeConfig = (
        campaign: string,
        productId: string,
        section: string,
        sessionId: string,
        storeId: string
    ) => {
        return {
            id: 'md',
            inputs: [
                {
                    name: 'store_id',
                    shape: [
                        1,
                    ],
                    datatype: 'BYTES',
                    data: [
                        storeId,
                    ],
                },
                {
                    name: 'session_id',
                    shape: [
                        1,
                    ],
                    datatype: 'BYTES',
                    data: [
                        sessionId,
                    ],
                },
                {
                    name: 'maxproducts',
                    shape: [
                        1,
                    ],
                    datatype: 'INT8',
                    data: [
                        '60',
                    ],
                },
                {
                    name: 'product_id',
                    shape: [
                        1,
                    ],
                    datatype: 'BYTES',
                    data: [
                        productId,
                    ],
                },
                {
                    name: 'campaign',
                    shape: [
                        1,
                    ],
                    datatype: 'BYTES',
                    data: [
                        campaign,
                    ],
                },
                {
                    name: 'cod_section',
                    shape: [
                        1,
                    ],
                    datatype: 'BYTES',
                    data: [
                        section,
                    ],
                },
            ],
        };
    };

    public getOtherYouMayLike$(
        campaign: string,
        productId: string,
        section: string,
        sessionId: string,
        storeId: string,
    ): Observable<ApiOtherYouMayLikeResponse> {
        const slug = `catalog/store/${storeId}/product/id/${productId}/related/personalized/yoda`;
        const url = this.requestService.requestUrl(slug, null, null, false, '4');
        const params = this.otherYouMayLikeConfig(campaign, productId, section, sessionId, storeId);

        return this.http.post<ApiOtherYouMayLikeResponse>(url, params);
    }
}
